import apiService from '../../../services/api-client';
import {
    ReclassifyBulkRequest,
    ReclassifyResponse,
} from '../interfaces/post-reclassify';
import {
    GetArticlesForReclassifyResponse,
    ReclassifyFilters,
} from '../interfaces/get-articles-for-reclassify';

export const reclassifyApiService = {
    async getArticlesForReclassify(
        companyId: number,
        filters: ReclassifyFilters = {}
    ): Promise<GetArticlesForReclassifyResponse> {
        const { 
            location_id,
            container_id,
            code, 
            provider_id, 
            origin_container_code, 
            description, 
            reference,
            status,
            page = 1 
        } = filters;

        let endpoint = `company/${companyId}/products/classified/lightweight?page=${page}`;

        // Filtros primarios
        if (location_id) {
            endpoint += `&location_id=${location_id}`;
        }

        if (container_id) {
            endpoint += `&container_id=${container_id}`;
        }

        // Filtros secundarios
        if (code) {
            endpoint += `&code=${code}`;
        }

        if (description) {
            endpoint += `&description=${description}`;
        }

        if (reference) {
            endpoint += `&reference=${reference}`;
        }

        if (status) {
            endpoint += `&status=${status}`;
        }

        if (origin_container_code) {
            endpoint += `&origin_container_code=${origin_container_code}`;
        }

        if (provider_id) {
            endpoint += `&provider_id=${provider_id}`;
        }

        console.log('Endpoint final para reclassify:', endpoint);
        const { data } = await apiService().get<GetArticlesForReclassifyResponse>(endpoint);
        return data;
    },

    async reclassifyArticles(request: ReclassifyBulkRequest): Promise<ReclassifyResponse> {
        const results: any[] = [];
        const errors: any[] = [];
        const companyId = request.companyId || 1; // Fallback a 1 si no se proporciona

        // Procesar cada artículo individualmente
        for (const item of request.items) {
            try {
                let response;
                let endpoint;

                if (item.to_container_id) {
                    // Mover a contenedor usando el endpoint correcto
                    endpoint = `company/${companyId}/container/${item.to_container_id}/productStorage/${item.article_id}`;
                    const body = {
                        quantity: item.quantity || 1,
                        status_id: item.status_id,
                        observation: item.observation || '',
                        // Campos de clasificación detallada
                        usage_id: item.usage_id,
                        usage_observation: item.usage_observation,
                        packaging_id: item.packaging_id,
                        packaging_observation: item.packaging_observation,
                        functionality_id: item.functionality_id,
                        functionality_observation: item.functionality_observation,
                        aesthetic_id: item.aesthetic_id,
                        aesthetic_observation: item.aesthetic_observation,
                        completeness_id: item.completeness_id,
                        completeness_observation: item.completeness_observation,
                        state_id: item.state_id,
                        reference: item.reference
                    };
                    console.log('Enviando request a contenedor:', endpoint, body);
                    response = await apiService().patch(endpoint, body);
                } else if (item.to_location_id) {
                    // Mover a ubicación usando el endpoint correcto
                    endpoint = `company/${companyId}/location/${item.to_location_id}/productStorage/${item.article_id}`;
                    const body = {
                        quantity: item.quantity || 1,
                        status_id: item.status_id,
                        observation: item.observation || '',
                        // Campos de clasificación detallada
                        usage_id: item.usage_id,
                        usage_observation: item.usage_observation,
                        packaging_id: item.packaging_id,
                        packaging_observation: item.packaging_observation,
                        functionality_id: item.functionality_id,
                        functionality_observation: item.functionality_observation,
                        aesthetic_id: item.aesthetic_id,
                        aesthetic_observation: item.aesthetic_observation,
                        completeness_id: item.completeness_id,
                        completeness_observation: item.completeness_observation,
                        state_id: item.state_id,
                        reference: item.reference
                    };
                    console.log('Enviando request a ubicación:', endpoint, body);
                    response = await apiService().patch(endpoint, body);
                } else {
                    throw new Error('Debe especificar un contenedor o ubicación de destino');
                }

                results.push({
                    article_id: item.article_id,
                    moved_quantity: item.quantity || 1,
                    from_container_id: item.from_container_id,
                    from_location_id: item.from_location_id,
                    to_container_id: item.to_container_id,
                    to_location_id: item.to_location_id,
                });

            } catch (error: any) {
                console.error(`Error reclassifying article ${item.article_id}:`, error);
                errors.push({
                    article_id: item.article_id,
                    error_message: error.response?.data?.message || error.message || 'Error desconocido',
                });
            }
        }

        // Construir respuesta
        const success = errors.length === 0;
        const message = success
            ? `Se reclasificaron ${results.length} artículo(s) exitosamente`
            : `Se reclasificaron ${results.length} artículo(s), ${errors.length} con errores`;

        return {
            success,
            message,
            reclassified_items: results,
            errors: errors.length > 0 ? errors : undefined,
        };
    },

    async getProductAuditReport(companyId: number, productStorageId: number) {
        const { data } = await apiService().get<{ movements: any[] }>(
            `company/${companyId}/products/classified/${productStorageId}/report`
        );
        return data.movements;
    },

    async getProductStates(companyId: number) {
        const { data } = await apiService().get<any[]>(
            `company/${companyId}/productStates`
        );
        return data;
    },

    async recodeProduct(companyId: number, productStorageId: number, request: any) {
        const { data } = await apiService().put(
            `company/${companyId}/products/classified/${productStorageId}`,
            request
        );
        return data;
    },

    async recodeProductV2(companyId: number, productStorageId: number, request: any) {
        const { data } = await apiService().put(
            `company/${companyId}/products/classified/${productStorageId}/v2`,
            request
        );
        return data;
    },
}; 